import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { FiArrowDown, FiGithub } from 'react-icons/fi';

const Hero = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const subheadingRef = useRef(null);
  const buttonsRef = useRef(null);
  const floatingCardsRef = useRef([]);
  const scrollIndicatorRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Staggered fade-in and slide-up for main content
      gsap.from(
        [headingRef.current, subheadingRef.current, buttonsRef.current],
        {
          opacity: 0,
          y: 50,
          duration: 1,
          stagger: 0.2,
          ease: 'power3.out',
        }
      );

      // Floating cards fade in from different directions
      floatingCardsRef.current.forEach((card, i) => {
        if (!card) return;
        const directions = [
          { x: -80, y: 40 },
          { x: 80, y: -30 },
          { x: -60, y: -50 },
          { x: 70, y: 60 },
        ];
        gsap.from(card, {
          opacity: 0,
          x: directions[i].x,
          y: directions[i].y,
          duration: 1.2,
          delay: 0.8 + i * 0.15,
          ease: 'power2.out',
        });
      });

      // Scroll indicator pulse
      gsap.from(scrollIndicatorRef.current, {
        opacity: 0,
        y: -10,
        duration: 1,
        delay: 1.6,
        ease: 'power2.out',
      });

      gsap.to(scrollIndicatorRef.current, {
        y: 10,
        duration: 1.5,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        delay: 2.2,
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const floatingCards = [
    {
      text: '10280 \u2192 Maharagama',
      position: 'top-[18%] left-[6%] md:left-[10%]',
      delay: 'animation-delay-0',
    },
    {
      text: 'Colombo District',
      position: 'top-[12%] right-[6%] md:right-[12%]',
      delay: 'animation-delay-1000',
    },
    {
      text: 'Western Province',
      position: 'bottom-[28%] left-[4%] md:left-[8%]',
      delay: 'animation-delay-2000',
    },
    {
      text: '11500 \u2192 Negombo',
      position: 'bottom-[22%] right-[4%] md:right-[10%]',
      delay: 'animation-delay-3000',
    },
  ];

  const handleGetStarted = (e) => {
    e.preventDefault();
    const demoSection = document.getElementById('demo');
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      ref={sectionRef}
      className="animated-gradient relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Ambient glow orbs */}
      <div className="hero-orb absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-primary-500/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="hero-orb absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-accent-500/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="hero-orb absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-ocean-500/5 rounded-full blur-[140px] pointer-events-none" />

      {/* Floating decorative cards */}
      {floatingCards.map((card, index) => (
        <div
          key={index}
          ref={(el) => (floatingCardsRef.current[index] = el)}
          className={`absolute ${card.position} hidden sm:block`}
          style={{ animationDelay: `${index * 1.5}s` }}
        >
          <div className="glass rounded-xl px-5 py-3 text-white/80 text-sm font-mono animate-float shadow-lg">
            <span className="text-primary-300">{card.text}</span>
          </div>
        </div>
      ))}

      {/* Main content */}
      <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <h1
          ref={headingRef}
          className="font-heading font-extrabold text-white text-5xl md:text-7xl leading-tight tracking-tight mb-6"
        >
          Unlock{' '}
          <span className="gradient-text">Sri Lankan</span>
          <br />
          Addresses
        </h1>

        <p
          ref={subheadingRef}
          className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed"
        >
          Complete postal code lookups, fuzzy search, and intelligent formatting
          for any SL address workflow
        </p>

        <div ref={buttonsRef} className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="#demo"
            onClick={handleGetStarted}
            className="group relative inline-flex items-center gap-2 bg-gradient-to-r from-primary-500 to-ocean-500 text-white font-semibold px-8 py-4 rounded-xl text-lg transition-all duration-300 hover:shadow-[0_0_30px_rgba(99,102,241,0.4)] hover:scale-105 active:scale-100"
          >
            Get Started
            <svg
              className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>

          <a
            href="https://github.com/sumudu-k/sl-address"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border-2 border-white/30 text-white font-semibold px-8 py-4 rounded-xl text-lg transition-all duration-300 hover:border-white/60 hover:bg-white/10 hover:scale-105 active:scale-100 backdrop-blur-sm"
          >
            <FiGithub className="w-5 h-5" />
            View on GitHub
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        ref={scrollIndicatorRef}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50"
      >
        <span className="text-xs uppercase tracking-widest font-medium">Scroll</span>
        <FiArrowDown className="w-5 h-5" />
      </div>
    </section>
  );
};

export default Hero;
